﻿import React, { useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { BookOpen, Download, Settings, Factory, ArrowUp, ArrowDown, X } from "lucide-react";
import { getCompany, useCompanyInfoVersion } from "../utils/companyInfo.js";
import { getFundamentals, useSummariesVersion } from "../utils/priceData.js";

const COLUMNS = [
  { id: "cmp", label: "CMP", unit: "$" },
  { id: "pe", label: "P/E", unit: "" },
  { id: "mcap", label: "Mar Cap", unit: "$B" },
  { id: "div", label: "Div Yld", unit: "%" },
  { id: "np", label: "NP Qtr", unit: "$M" },
  { id: "npVar", label: "Qtr Profit Var", unit: "%" },
  { id: "sales", label: "Sales Qtr", unit: "$M" },
  { id: "salesVar", label: "Qtr Sales Var", unit: "%" },
  { id: "roce", label: "ROCE", unit: "%" },
];

export default function WatchlistTable({ list, onAdd, onRemove }) {
  const infoVersion = useCompanyInfoVersion();
  const sumVersion = useSummariesVersion();
  const [hidden, setHidden] = useState([]);
  const [industry, setIndustry] = useState("all");
  const [sort, setSort] = useState({ col: null, dir: "asc" });

  const rows = useMemo(
    () =>
      list.tickers
        .map((t) => getCompany(t))
        .filter(Boolean)
        .map((c) => ({ ...c, f: getFundamentals(c.ticker) })),
    [list.tickers, infoVersion, sumVersion]
  );

  const industries = [...new Set(rows.map((r) => r.category))];
  const visibleCols = COLUMNS.filter((c) => !hidden.includes(c.id));

  const filtered = rows.filter((r) => industry === "all" || r.category === industry);
  const sorted = sort.col
    ? [...filtered].sort((a, b) => {
        const av = sort.col === "company" ? a.name : a.f[sort.col];
        const bv = sort.col === "company" ? b.name : b.f[sort.col];
        const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        return sort.dir === "asc" ? cmp : -cmp;
      })
    : filtered;

  const toggleSort = (col) =>
    setSort((s) =>
      s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }
    );

  const toggleColumn = (id) =>
    setHidden((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]));

  const exportCsv = () => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const header = ["S.No.", "Company", "Ticker", "Industry", ...visibleCols.map((c) => `${c.label} ${c.unit}`.trim())];
    const lines = sorted.map((r, i) =>
      [i + 1, r.name, r.ticker, r.category, ...visibleCols.map((c) => r.f[c.id].toFixed(2))]
        .map(esc)
        .join(",")
    );
    const blob = new Blob([[header.map(esc).join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${list.name}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SortIcon = ({ col }) =>
    sort.col === col ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : null;

  return (
    <div className="card wl-table-card">
      {/* Toolbar */}
      <div className="wl-toolbar">
        <Dropdown>
          <Dropdown.Toggle variant="" className="tool-btn">
            <Factory size={15} /> {industry === "all" ? "INDUSTRY" : industry.toUpperCase()}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item active={industry === "all"} onClick={() => setIndustry("all")}>
              All industries
            </Dropdown.Item>
            {industries.map((ind) => (
              <Dropdown.Item key={ind} active={industry === ind} onClick={() => setIndustry(ind)}>
                {ind}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <button className="btn tool-btn" onClick={exportCsv} disabled={rows.length === 0}>
          <Download size={15} /> EXPORT
        </button>

        <Dropdown align="end" autoClose="outside">
          <Dropdown.Toggle variant="" className="tool-btn tool-btn-primary">
            <Settings size={15} /> EDIT COLUMNS
          </Dropdown.Toggle>
          <Dropdown.Menu className="columns-menu">
            {COLUMNS.map((c) => (
              <label key={c.id} className="columns-item">
                <input
                  type="checkbox"
                  checked={!hidden.includes(c.id)}
                  onChange={() => toggleColumn(c.id)}
                />
                {c.label} {c.unit}
              </label>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table wl-table mb-0">
          <thead>
            <tr>
              <th className="wl-th-plain">S.No.</th>
              <th></th>
              <th>
                <button className="th-btn" onClick={() => toggleSort("company")}>
                  Company <SortIcon col="company" />
                </button>
              </th>
              {visibleCols.map((c) => (
                <th key={c.id} className="text-end">
                  <button className="th-btn" onClick={() => toggleSort(c.id)}>
                    {c.label} <span className="th-unit">{c.unit}</span> <SortIcon col={c.id} />
                  </button>
                </th>
              ))}
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.ticker}>
                <td>{i + 1}.</td>
                <td><BookOpen size={15} className="wl-book" /></td>
                <td>
                  <a className="wl-company" href={`#/stock/${r.ticker}`}>{r.name}</a>
                  <span className="text-muted small ms-2">{r.ticker}</span>
                </td>
                {visibleCols.map((c) => (
                  <td key={c.id} className="text-end">{r.f[c.id].toFixed(2)}</td>
                ))}
                <td className="text-end">
                  <button
                    className="row-remove"
                    onClick={() => onRemove(r.ticker)}
                    title="Remove from watchlist"
                    aria-label={`Remove ${r.name}`}
                  >
                    <X size={15} />
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={visibleCols.length + 4} className="wl-empty">
                  <p className="text-muted mb-3">
                    {rows.length === 0
                      ? "This watchlist is empty. Add companies to start tracking them."
                      : "No companies match this industry."}
                  </p>
                  {rows.length === 0 && (
                    <button className="btn btn-purple" onClick={onAdd}>ADD COMPANIES</button>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-muted small wl-note">Prices are about 15 minutes delayed. Financial figures come from SEC filings. Companies without standard SEC financials (SPACs, funds, some foreign filers) show a dash.</p>
    </div>
  );
}






